import { useState, useCallback } from 'react';

import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';

import { merchantService } from '@services/operator/merchantService';

/**
 * Hook for managing OrderItem secondary columns configuration for Operator
 *
 * @returns {Object} - { enabledColumns, availableColumns, loading, updateColumns }
 */
const useOrderItemColumns = () => {
  const { currentMerchant, updateOrderItemColumns, orderItemColumns, availableOrderItemColumns } = useOperatorData();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  // Update columns configuration
  // overrideMerchantId: 當 currentMerchant 未設定時（例如訂單編輯頁面），可透過此參數傳入貨主 ID
  const updateColumns = useCallback(async (columns, overrideMerchantId = null) => {
    const merchantId = currentMerchant?.id || overrideMerchantId;
    if (!merchantId) {
      message.error(t('shared.orders.order_items.please_select_merchant'));
      return false;
    }

    setLoading(true);
    try {
      const data = await merchantService.updateOrderItemColumns(merchantId, columns);

      if (data.success) {
        // 只更新 Context 的 orderItemColumns，不觸發整個 currentMerchant 更新
        updateOrderItemColumns(data.data.order_item_columns || []);
        message.success(t('shared.orders.order_items.columns_saved'));
        return true;
      }
      message.error(data.message || t('shared.orders.order_items.columns_save_failed'));
      return false;

    } catch (error) {
      console.error('Failed to update columns:', error);
      message.error(t('shared.orders.order_items.columns_save_failed'));
      return false;
    } finally {
      setLoading(false);
    }
  }, [currentMerchant?.id, updateOrderItemColumns, t]);

  return {
    enabledColumns: orderItemColumns,
    availableColumns: availableOrderItemColumns,
    loading,
    updateColumns
  };
};

export default useOrderItemColumns;
